import { Api } from '@/api/api.client';
import type { Components } from '@/api/generated/api';
import { useCurrentTenant } from '@/composables/schoolAdmin/useCurrentTenant';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useAuthStore } from '@/stores/useAuthStore';
import type { QueryKey } from '@tanstack/vue-query';
import { computed, type ComputedRef } from 'vue';
import { billingKeys } from './billing.keys';

interface IBillingStatusComposable {
  billingStatus: ComputedRef<Components.Schemas.BillingStatusDto | undefined>;
  billingStatusQueryKey: ComputedRef<QueryKey>;
  isBillingStatusLoading: ComputedRef<boolean>;
}

type UseBillingStatusOptions = {
  currentTenant?: ComputedRef<Components.Schemas.TenantDto | null>;
};

export function useBillingStatus(options: UseBillingStatusOptions = {}): IBillingStatusComposable {
  const authStore = useAuthStore();
  const currentTenant = options.currentTenant ?? useCurrentTenant().currentTenant;

  const billingStatusQueryKey = computed<QueryKey>(() => billingKeys.status(currentTenant.value?.id ?? null));

  const getBillingStatus = async (): Promise<Components.Schemas.BillingStatusDto> => {
    const result = await Api.Routes.Billing.status({});
    return result.data;
  };

  const queryBillingStatus = useQueryWithToast({
    queryKey: billingStatusQueryKey,
    queryFn: getBillingStatus,
    enabled: computed(() => authStore.csrfToken != null && currentTenant.value != null),
  });

  const billingStatus = computed<Components.Schemas.BillingStatusDto | undefined>(() => {
    if (authStore.csrfToken == null || currentTenant.value == null) return undefined;
    return queryBillingStatus.data.value ?? undefined;
  });

  // True only while the initial fetch is in flight. A disabled query (no csrf/tenant) reports false, so
  // callers fall back to the fail-open feature defaults rather than waiting forever.
  const isBillingStatusLoading = computed<boolean>(() => queryBillingStatus.isLoading.value);

  return {
    billingStatus,
    billingStatusQueryKey,
    isBillingStatusLoading,
  };
}
